import React, { useEffect, useState } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { Loader2, Save, Eye, FileText, ScrollText, RotateCcw } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLegalEditor() {
  const [docs, setDocs] = useState([]);
  const [activeSlug, setActiveSlug] = useState("terms");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [drafts, setDrafts] = useState({}); // { slug: {title, content_md} }

  const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/legal`, { headers })
      .then((res) => {
        if (!cancelled) {
          const list = res.data?.documents || [];
          setDocs(list);
          const initial = {};
          list.forEach((d) => {
            initial[d.slug] = { title: d.title, content_md: d.content_md || "" };
          });
          setDrafts(initial);
        }
      })
      .catch((e) => {
        if (!cancelled) toast.error(e.response?.data?.detail || "Failed to load legal documents");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateDraft = (slug, patch) => {
    setDrafts((prev) => ({ ...prev, [slug]: { ...(prev[slug] || {}), ...patch } }));
  };

  const saveDoc = async (slug) => {
    const draft = drafts[slug];
    if (!draft) return;
    if (!draft.title?.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/legal/${slug}`,
        { title: draft.title, content_md: draft.content_md },
        { headers }
      );
      toast.success(`${res.data.title} saved (v${res.data.version})`);
      setDocs((prev) => prev.map((d) => (d.slug === slug ? res.data : d)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async (slug) => {
    if (!window.confirm(
      `Replace the current content of "${drafts[slug]?.title || slug}" with the built-in default template?\n\n` +
      `This will overwrite your current edits for this document only. The change is logged and the version is bumped, ` +
      `but the previous version is not automatically backed up — copy anything you want to keep first.`
    )) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/legal/${slug}/reset-to-default`, {}, { headers });
      const doc = res.data?.document;
      if (!doc) throw new Error("Bad response");
      toast.success(`${doc.title} reset to default (v${doc.version})`);
      setDocs((prev) => prev.map((d) => (d.slug === slug ? doc : d)));
      setDrafts((prev) => ({ ...prev, [slug]: { title: doc.title, content_md: doc.content_md || "" } }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[300px]" data-testid="admin-legal-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const ICONS = { terms: ScrollText, privacy: FileText, "sub-processors": FileText };

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto" data-testid="admin-legal-editor">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
          <ScrollText className="h-8 w-8 text-primary" />
          Legal Documents
        </h1>
        <p className="text-muted-foreground mt-1">
          Edit your Terms of Service and Privacy Policy. Changes go live immediately at <code>/legal/&lt;slug&gt;</code>.
        </p>
      </div>

      <Tabs value={activeSlug} onValueChange={setActiveSlug}>
        <TabsList>
          {docs.map((d) => {
            const Icon = ICONS[d.slug] || FileText;
            return (
              <TabsTrigger key={d.slug} value={d.slug} data-testid={`legal-tab-${d.slug}`}>
                <Icon className="h-4 w-4 mr-2" />
                {d.title}
                <Badge variant="outline" className="ml-2">v{d.version}</Badge>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {docs.map((d) => {
          const draft = drafts[d.slug] || { title: d.title, content_md: d.content_md };
          return (
            <TabsContent key={d.slug} value={d.slug} className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between flex-wrap gap-2">
                    <span>Edit “{draft.title}”</span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => resetToDefault(d.slug)}
                        disabled={saving}
                        data-testid={`reset-legal-${d.slug}-btn`}
                        title="Replace the current content with the built-in default template"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Reset to Default
                      </Button>
                      <Button
                        onClick={() => saveDoc(d.slug)}
                        disabled={saving}
                        data-testid={`save-legal-${d.slug}-btn`}
                      >
                        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                        Save Changes
                      </Button>
                    </div>
                  </CardTitle>
                  <CardDescription>
                    Last updated {d.updated_at ? new Date(d.updated_at).toLocaleString() : "—"}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-1">
                    <Label>Title</Label>
                    <Input
                      value={draft.title}
                      onChange={(e) => updateDraft(d.slug, { title: e.target.value })}
                      data-testid={`legal-title-${d.slug}`}
                    />
                  </div>

                  <Tabs defaultValue="edit">
                    <TabsList>
                      <TabsTrigger value="edit"><FileText className="h-3.5 w-3.5 mr-1.5" /> Edit (Markdown)</TabsTrigger>
                      <TabsTrigger value="preview"><Eye className="h-3.5 w-3.5 mr-1.5" /> Preview</TabsTrigger>
                    </TabsList>
                    <TabsContent value="edit">
                      <Textarea
                        rows={24}
                        className="font-mono text-sm"
                        value={draft.content_md}
                        onChange={(e) => updateDraft(d.slug, { content_md: e.target.value })}
                        data-testid={`legal-content-${d.slug}`}
                      />
                    </TabsContent>
                    <TabsContent value="preview">
                      <article className="prose prose-slate dark:prose-invert max-w-none border rounded-md p-4 min-h-[400px]">
                        <ReactMarkdown>{draft.content_md || ""}</ReactMarkdown>
                      </article>
                    </TabsContent>
                  </Tabs>

                  <p className="text-xs text-muted-foreground">
                    Public link: <code>/legal/{d.slug}</code> — open in a new tab to verify.
                  </p>
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}
